<script setup lang="ts">
import type { TenantLandingConfig, TenantBrandingConfig } from '~/types/types'
import { youtubeEmbed } from '~/composables/useLandingTemplate'

/**
 * Tenant-authored content sections.
 *  - alternating: media/text zig-zag rows (Classic / Split / Bold)
 *  - numbered:    single narrow column with 01/02/… markers — used by the
 *                 Editorial template where sections describe the agency's
 *                 process, so order carries meaning
 *  - plain:       stacked, centered, no media emphasis (Minimal)
 */
const props = withDefaults(defineProps<{
  landing: TenantLandingConfig
  branding: TenantBrandingConfig
  variant?: 'alternating' | 'numbered' | 'plain'
  displayClass?: string
}>(), { variant: 'alternating', displayClass: '' })

// Design: text-only sections read as service cards ("Where we place"),
// media sections as zig-zag story rows. All-text pages get a pure card
// grid; mixed pages keep author order but render text-only entries as
// full-width cards.
const hasAnyMedia = computed(() =>
  props.landing.content_sections.some(s => s.media_url))
</script>

<template>
  <template v-if="landing.content_sections.length">
    <!-- all text-only: service card grid ("Where we place") -->
    <section
      v-if="variant === 'alternating' && !hasAnyMedia"
      class="mx-auto max-w-6xl px-6 py-20"
    >
      <div class="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
        <div
          v-for="(section, i) in landing.content_sections"
          :key="section.id || i"
          class="site-surface-card p-6"
        >
          <h3
            class="text-xl tracking-tight"
            :class="displayClass"
          >
            {{ section.title }}
          </h3>
          <p
            v-if="section.detail"
            class="mt-3 text-sm ui-text-subtle whitespace-pre-line leading-relaxed"
          >
            {{ section.detail }}
          </p>
          <a
            v-if="section.media_link"
            :href="section.media_link"
            target="_blank"
            rel="noopener"
            class="site-text-accent mt-4 inline-flex items-center gap-1 text-sm font-medium"
          >
            {{ $t('site.more_info') }} <UIcon
              name="i-lucide-arrow-right"
              class="size-4"
            />
          </a>
        </div>
      </div>
    </section>

    <!-- alternating media/text rows; text-only entries become full cards -->
    <section
      v-else-if="variant === 'alternating'"
      class="mx-auto max-w-6xl px-6 py-20 space-y-16"
    >
      <div
        v-for="(section, i) in landing.content_sections"
        :key="section.id || i"
        :class="section.media_url
          ? ['grid gap-10 lg:grid-cols-2 items-center', { 'lg:[&>*:first-child]:order-2': i % 2 === 1 }]
          : 'site-surface-card p-8 sm:p-10'"
      >
        <div>
          <h3
            class="text-2xl tracking-tight"
            :class="displayClass"
          >
            {{ section.title }}
          </h3>
          <p
            v-if="section.detail"
            class="mt-3 text-base ui-text-subtle whitespace-pre-line leading-relaxed"
            :class="{ 'max-w-2xl': !section.media_url }"
          >
            {{ section.detail }}
          </p>
          <a
            v-if="section.media_link"
            :href="section.media_link"
            target="_blank"
            rel="noopener"
            class="site-text-accent mt-4 inline-flex items-center gap-1 text-sm font-medium"
          >
            {{ $t('site.more_info') }} <UIcon
              name="i-lucide-arrow-right"
              class="size-4"
            />
          </a>
        </div>
        <div
          v-if="section.media_url"
          class="rounded-[var(--site-radius)] overflow-hidden aspect-video bg-[var(--bg2)]"
        >
          <iframe
            v-if="section.media_type === 'youtube' && youtubeEmbed(section.media_url)"
            :src="youtubeEmbed(section.media_url)!"
            class="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          />
          <img
            v-else
            :src="section.media_url"
            :alt="section.title"
            class="w-full h-full object-cover"
          >
        </div>
      </div>
    </section>

    <!-- numbered narrow column -->
    <section
      v-else-if="variant === 'numbered'"
      class="mx-auto max-w-3xl px-6 py-16"
    >
      <div
        v-for="(section, i) in landing.content_sections"
        :key="section.id || i"
        class="border-t border-[var(--border)] py-10 first:border-t-0"
      >
        <div class="flex items-baseline gap-4">
          <span
            class="text-sm font-semibold tabular-nums site-text-accent"
          >
            {{ String(i + 1).padStart(2, '0') }}
          </span>
          <div class="min-w-0">
            <h3
              class="text-xl sm:text-2xl font-semibold tracking-tight"
              :class="displayClass"
            >
              {{ section.title }}
            </h3>
            <p
              v-if="section.detail"
              class="mt-3 text-base ui-text-subtle whitespace-pre-line leading-relaxed"
            >
              {{ section.detail }}
            </p>
            <div
              v-if="section.media_url"
              class="mt-5 overflow-hidden rounded-lg aspect-video bg-[var(--bg2)]"
            >
              <iframe
                v-if="section.media_type === 'youtube' && youtubeEmbed(section.media_url)"
                :src="youtubeEmbed(section.media_url)!"
                class="w-full h-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowfullscreen
              />
              <img
                v-else
                :src="section.media_url"
                :alt="section.title"
                class="w-full h-full object-cover"
              >
            </div>
            <a
              v-if="section.media_link"
              :href="section.media_link"
              target="_blank"
              rel="noopener"
              class="mt-4 inline-flex items-center gap-1 text-sm font-medium"
            >
              {{ $t('site.more_info') }} <UIcon
                name="i-lucide-arrow-right"
                class="size-4"
              />
            </a>
          </div>
        </div>
      </div>
    </section>

    <!-- plain centered stack -->
    <section
      v-else
      class="mx-auto max-w-2xl px-6 py-16 space-y-16 text-center"
    >
      <div
        v-for="(section, i) in landing.content_sections"
        :key="section.id || i"
      >
        <h3
          class="text-xl sm:text-2xl font-medium tracking-tight"
          :class="displayClass"
        >
          {{ section.title }}
        </h3>
        <p
          v-if="section.detail"
          class="mt-3 text-base ui-text-subtle whitespace-pre-line leading-relaxed"
        >
          {{ section.detail }}
        </p>
        <div
          v-if="section.media_url"
          class="mt-6 overflow-hidden rounded-lg aspect-video bg-[var(--bg2)]"
        >
          <iframe
            v-if="section.media_type === 'youtube' && youtubeEmbed(section.media_url)"
            :src="youtubeEmbed(section.media_url)!"
            class="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          />
          <img
            v-else
            :src="section.media_url"
            :alt="section.title"
            class="w-full h-full object-cover"
          >
        </div>
        <a
          v-if="section.media_link"
          :href="section.media_link"
          target="_blank"
          rel="noopener"
          class="mt-4 inline-flex items-center gap-1 text-sm font-medium"
        >
          {{ $t('site.more_info') }} <UIcon
            name="i-lucide-arrow-right"
            class="size-4"
          />
        </a>
      </div>
    </section>
  </template>
</template>
